import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { Trophy, Flame, User, Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { BadgeDisplay } from "@/components/BadgeDisplay";
import { GeminiHeader } from "@/components/GeminiHeader";
import { XPHistory } from "@/components/XPHistory";
import { motion } from "framer-motion";
import { Capacitor } from "@capacitor/core";

const Profile = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [profile, setProfile] = useState<any>(null);
  const [displayName, setDisplayName] = useState("");
  const [fullName, setFullName] = useState("");
  const [position, setPosition] = useState("");
  const [team, setTeam] = useState("");
  const [isSendingTestPush, setIsSendingTestPush] = useState(false);
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/signup");
      return;
    }

    const { data } = await supabase.from("worker_profiles").select("*").eq("user_id", user.id).single();

    if (data) {
      setProfile(data);
      setDisplayName(data.display_name || "");
      setFullName(data.full_name || "");
      setPosition(data.position || "");
      setTeam(data.team || "");
    }
  };

  const handleSave = async () => {
    if (!profile) return;

    const { error } = await supabase
      .from("worker_profiles")
      .update({ display_name: displayName, full_name: fullName, position, team })
      .eq("id", profile.id);

    if (error) {
      toast.error("Failed to save profile");
    } else {
      toast.success(t("profile.saved"));
      navigate("/home");
    }
  };

  const handleTestPush = async () => {
    setIsSendingTestPush(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Not authenticated");
        return;
      }

      const response = await supabase.functions.invoke('test-push', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      console.log('[Profile] Test push response:', response);

      if (response.error) {
        toast.error(`Test push failed: ${response.error.message}`);
        return;
      }

      const data = response.data;
      if (data.success) {
        toast.success("Test notification sent! Check your device.");
      } else if (data.error === 'no_token') {
        toast.error("No push token saved. The app may not have registered correctly.");
        console.log('[Profile] Push debug info:', data.profile);
      } else {
        toast.error(`Push failed: ${data.message || data.error}`);
        console.log('[Profile] Push result:', data.push_result);
      }
    } catch (error) {
      console.error('[Profile] Test push error:', error);
      toast.error("Failed to send test push");
    } finally {
      setIsSendingTestPush(false);
    }
  };

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-background">
      <GeminiHeader title={t("profile.title")} subtitle="Update your information" />

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 gap-3 mb-6"
        >
          <div className="rounded-2xl p-4 border border-border bg-card text-center">
            <div className="p-2 rounded-xl bg-success/10 w-fit mx-auto mb-2">
              <Trophy className="h-5 w-5 text-success" />
            </div>
            <div className="text-2xl font-bold text-success">{profile.total_xp || 0}</div>
            <div className="text-xs text-muted-foreground">Total XP</div>
          </div>
          <div className="rounded-2xl p-4 border border-border bg-card text-center">
            <div className="p-2 rounded-xl bg-warning/10 w-fit mx-auto mb-2">
              <Flame className="h-5 w-5 text-warning" />
            </div>
            <div className="text-2xl font-bold text-warning">{profile.consecutive_safe_days || 0}</div>
            <div className="text-xs text-muted-foreground">Streak</div>
          </div>
        </motion.div>

        {/* XP History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="rounded-2xl p-5 border border-border bg-card mb-6"
        >
          <XPHistory workerId={profile.id} compact />
        </motion.div>

        {/* Badges */}
        {profile.badges && profile.badges.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-2xl p-5 border border-border bg-card mb-6"
          >
            <h3 className="font-bold mb-3">Badges Earned</h3>
            <BadgeDisplay badges={profile.badges} />
          </motion.div>
        )}

        {/* Profile Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="rounded-2xl p-6 border border-border bg-card space-y-5"
        >
          <div className="flex items-center gap-3 pb-4 border-b border-border">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <User className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-bold">Profile Details</h3>
              <p className="text-sm text-muted-foreground">Update your information</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayName">{t("profile.name")}</Label>
            <Input 
              id="displayName" 
              value={displayName} 
              onChange={(e) => setDisplayName(e.target.value)} 
              className="h-12 rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fullName">Full Name</Label>
            <Input 
              id="fullName" 
              value={fullName} 
              onChange={(e) => setFullName(e.target.value)} 
              className="h-12 rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="position">Position</Label>
            <Input 
              id="position" 
              value={position} 
              onChange={(e) => setPosition(e.target.value)} 
              className="h-12 rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="team">{t("profile.team")}</Label>
            <Input 
              id="team" 
              value={team} 
              onChange={(e) => setTeam(e.target.value)} 
              className="h-12 rounded-xl"
            />
          </div>

          <Button 
            onClick={handleSave} 
            className="w-full h-12 rounded-xl"
          >
            {t("profile.save")}
          </Button>

          {/* Test Push Button - only show on native platforms */}
          {isNative && (
            <Button 
              onClick={handleTestPush}
              disabled={isSendingTestPush}
              variant="outline"
              className="w-full h-12 rounded-xl border-dashed"
            >
              {isSendingTestPush ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Bell className="h-4 w-4 mr-2" />
              )}
              Test Push Notification
            </Button>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default Profile;